import { useState, useEffect } from 'react';
import { useSeoMeta } from '@unhead/react';
import { Settings, Key, Database, Cloud, Eye, EyeOff, Save, Loader2, CheckCircle2, RefreshCw } from 'lucide-react';
import { AppLayout, PageBreadcrumb } from '@/components/AppLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Slider } from '@/components/ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { LoginArea } from '@/components/auth/LoginArea';
import { useAppSettings, useSaveAppSettings } from '@/hooks/useAppSettings';
import { useSyncFromNostr } from '@/hooks/useEvaluations';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import type { AppSettings, ApiProvider } from '@/types/stock';
import { DEFAULT_APP_SETTINGS } from '@/types/stock';
import { useToast } from '@/hooks/useToast';

export default function SettingsPage() {
  useSeoMeta({ title: 'Settings — Stock Fundamentals' });

  const { data: saved } = useAppSettings();
  const { mutateAsync: saveSettings, isPending: isSaving } = useSaveAppSettings();
  const { mutate: syncNostr, isPending: isSyncing } = useSyncFromNostr();
  const { user } = useCurrentUser();
  const { toast } = useToast();

  const [settings, setSettings] = useState<AppSettings>(DEFAULT_APP_SETTINGS);
  const [showAvKey, setShowAvKey] = useState(false);
  const [showFmpKey, setShowFmpKey] = useState(false);
  const [saved2, setSaved2] = useState(false);

  useEffect(() => {
    if (saved) setSettings(saved);
  }, [saved]);

  const handleSave = async () => {
    await saveSettings(settings);
    setSaved2(true);
    toast({ title: 'Settings saved', description: 'Your configuration has been updated.' });
    setTimeout(() => setSaved2(false), 2000);
  };

  const updateApi = (key: keyof typeof settings.apiSettings, value: string) => {
    setSettings((s) => ({ ...s, apiSettings: { ...s.apiSettings, [key]: value } }));
  };

  return (
    <AppLayout>
      <div className="max-w-screen-lg mx-auto px-4 py-8">
        <PageBreadcrumb items={[{ label: 'Dashboard', href: '/' }, { label: 'Settings' }]} />

        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
              <Settings className="w-7 h-7 text-primary" /> Settings
            </h1>
            <p className="text-muted-foreground mt-1">Configure API keys, analysis defaults, and sync preferences.</p>
          </div>
          <Button onClick={handleSave} disabled={isSaving} className="gap-2">
            {isSaving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : saved2 ? (
              <CheckCircle2 className="w-4 h-4 text-emerald-500" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            {saved2 ? 'Saved!' : 'Save Settings'}
          </Button>
        </div>

        <div className="space-y-6">
          {/* API Configuration */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Key className="w-4 h-4" /> API Configuration
              </CardTitle>
              <CardDescription>
                Connect a financial data provider to automatically fetch stock data.
                <strong className="text-foreground"> Alpha Vantage is recommended</strong> — it offers a genuinely free tier with no credit card required.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="space-y-2">
                <Label>Active Provider</Label>
                <Select
                  value={settings.apiSettings.provider}
                  onValueChange={(v) => updateApi('provider', v as ApiProvider)}
                >
                  <SelectTrigger className="w-64">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None (CSV upload only)</SelectItem>
                    <SelectItem value="alpha_vantage">Alpha Vantage — free tier (recommended)</SelectItem>
                    <SelectItem value="fmp">Financial Modeling Prep — paid plan required</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Separator />

              {/* Alpha Vantage */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="av-key">Alpha Vantage API Key</Label>
                  <a
                    href="https://www.alphavantage.co/support/#api-key"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-primary hover:underline"
                  >
                    Get free key →
                  </a>
                </div>
                <div className="flex gap-2">
                  <Input
                    id="av-key"
                    type={showAvKey ? 'text' : 'password'}
                    placeholder="Enter your Alpha Vantage API key"
                    value={settings.apiSettings.alphaVantageKey}
                    onChange={(e) => updateApi('alphaVantageKey', e.target.value)}
                    className="font-mono"
                  />
                  <Button variant="outline" size="icon" onClick={() => setShowAvKey(!showAvKey)}>
                    {showAvKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Genuinely free: 25 requests/day, no credit card required. Data is cached 24 hours to conserve quota.
                </p>
              </div>

              <Separator />

              {/* FMP */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="fmp-key">Financial Modeling Prep API Key</Label>
                  <a
                    href="https://financialmodelingprep.com/developer/docs"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-primary hover:underline"
                  >
                    View plans →
                  </a>
                </div>
                <div className="flex gap-2">
                  <Input
                    id="fmp-key"
                    type={showFmpKey ? 'text' : 'password'}
                    placeholder="Enter your FMP API key"
                    value={settings.apiSettings.fmpKey}
                    onChange={(e) => updateApi('fmpKey', e.target.value)}
                    className="font-mono"
                  />
                  <Button variant="outline" size="icon" onClick={() => setShowFmpKey(!showFmpKey)}>
                    {showFmpKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                </div>
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  ⚠️ Financial statement endpoints (income, balance sheet, cash flow) require a paid FMP plan.
                  If you get a 403 error, switch to Alpha Vantage or use CSV upload instead.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Analysis Defaults */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Database className="w-4 h-4" /> Analysis Defaults
              </CardTitle>
              <CardDescription>
                Default values used when running a new analysis.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Default Lookback Period</Label>
                  <span className="text-sm font-mono font-bold text-primary">
                    {settings.defaultLookbackYears} years
                  </span>
                </div>
                <Slider
                  min={5} max={10} step={1}
                  value={[settings.defaultLookbackYears]}
                  onValueChange={([v]) => setSettings((s) => ({ ...s, defaultLookbackYears: v }))}
                />
              </div>

              <Separator />

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Default Net Profit Margin Target</Label>
                  <span className="text-sm font-mono font-bold text-primary">
                    {settings.defaultProfitMarginTarget}%
                  </span>
                </div>
                <Slider
                  min={5} max={30} step={1}
                  value={[settings.defaultProfitMarginTarget]}
                  onValueChange={([v]) => setSettings((s) => ({ ...s, defaultProfitMarginTarget: v }))}
                />
                <p className="text-xs text-muted-foreground">Minimum average net margin to pass the profit margin criterion.</p>
              </div>

              <Separator />

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Default Current Ratio Target</Label>
                  <span className="text-sm font-mono font-bold text-primary">
                    {settings.defaultCurrentRatioTarget.toFixed(1)}x
                  </span>
                </div>
                <Slider
                  min={0.5} max={3.0} step={0.1}
                  value={[settings.defaultCurrentRatioTarget]}
                  onValueChange={([v]) => setSettings((s) => ({ ...s, defaultCurrentRatioTarget: v }))}
                />
                <p className="text-xs text-muted-foreground">Minimum current ratio (current assets / current liabilities) to pass.</p>
              </div>

              <Separator />

              <div className="space-y-2">
                <Label>Benchmark Index</Label>
                <Select
                  value={settings.benchmarkSymbol}
                  onValueChange={(v) => setSettings((s) => ({ ...s, benchmarkSymbol: v }))}
                >
                  <SelectTrigger className="w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="SPY">S&P 500 (SPY)</SelectItem>
                    <SelectItem value="QQQ">NASDAQ-100 (QQQ)</SelectItem>
                    <SelectItem value="DIA">Dow Jones (DIA)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Nostr Sync */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Cloud className="w-4 h-4" /> Nostr Sync
              </CardTitle>
              <CardDescription>
                Log in with your Nostr identity to sync evaluations across devices.
                All evaluations are stored as Kind 30078 parameterized replaceable events.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4">
                <LoginArea className="max-w-64" />
              </div>

              {user && (
                <>
                  <Alert className="border-emerald-200 bg-emerald-50 dark:bg-emerald-950/20">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    <AlertDescription className="text-emerald-700 dark:text-emerald-300">
                      Logged in as <code className="font-mono text-xs">{user.pubkey.slice(0, 16)}…</code>
                      New evaluations will automatically sync to your relays.
                    </AlertDescription>
                  </Alert>
                  <Button
                    variant="outline"
                    className="gap-2"
                    onClick={() => syncNostr()}
                    disabled={isSyncing}
                  >
                    {isSyncing ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4" />
                    )}
                    Sync evaluations from Nostr
                  </Button>
                  <p className="text-xs text-muted-foreground">
                    This pulls your saved evaluations from Nostr relays into local storage.
                    Useful when switching devices.
                  </p>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </AppLayout>
  );
}
